import React, { Component } from "react";
import PropTypes from "prop-types";
import { observer } from "mobx-react";
import { Grid, Modal, Header, Button, Icon, Message } from "semantic-ui-react";
import classNames from "classnames";
import ErrorWrapper from "~/components/wrapper/ErrorWrapper";
import ReviewImagesStore from "../stores/ReviewImagesStore";
import StepsHeader, { Steps } from "../components/StepsHeader";
import ReviewImages from "./ReviewImagesPage/ReviewImages";
import Toolbar from "./ReviewImagesPage/Toolbar";
import PromiseButton from "~/components/element/PromiseButton";
import ContentLoader from "~/components/element/ContentLoader";
import JobBreadcrumb from "../components/JobBreadcrumb";
import "../styles/ReviewImages.less";

@observer
class ReviewImagesPage extends Component {
  reviewImagesStore = null;

  constructor(props) {
    super(props);
    const { match, photoEditingJobId } = props;

    this.reviewImagesStore = new ReviewImagesStore(photoEditingJobId || match.params.photoEditingJobId);

    this.state = {
      modalOpen: false,
      modalPromptForMoreExposuresOpen: false,
    };
  }

  componentDidMount() {
    const { showError } = this.props;

    this.reviewImagesStore.load().catch(error => showError(error.message));
  }

  continue() {
    const { continueCallback } = this.props;
    const {
      groupedByPhotosPerImage,
      isSaving,
      isSavingPhotosPerImage,
      notEnoughExposuresPerImage,
    } = this.reviewImagesStore;

    if (isSaving || isSavingPhotosPerImage) {
      return;
    }

    if (notEnoughExposuresPerImage) {
      this.openPromptForMoreExposuresModal();

      return;
    }

    if (!groupedByPhotosPerImage) {
      this.reviewImagesStore.setHasPhotosPerImageErrors(true);
      this.openModal();

      return;
    }

    continueCallback();
  }

  openModal() {
    this.setState({ modalOpen: true });
  }

  closeModal() {
    this.setState({ modalOpen: false });
  }

  openPromptForMoreExposuresModal() {
    this.setState({ modalPromptForMoreExposuresOpen: true });
  }

  closePromptForMoreExposuresModal() {
    this.setState({ modalPromptForMoreExposuresOpen: false });
  }

  render() {
    const {
      isLoading,
      photoEditingJobId,
      photoEditingImages,
      photosPerImage,
      isSaving,
      isSavingPhotosPerImage,
      hasPhotosPerImageErrors,
      hasSelectedPhotos,
      hasPhotos,
      notEnoughExposuresPerImage,
    } = this.reviewImagesStore;
    const {
      showError,
      stepsHeaderClickCallback,
      viewJobsCallback,
      showUploadPageCallback,
      continueCallback,
    } = this.props;
    const { modalOpen, modalPromptForMoreExposuresOpen } = this.state;

    if (isLoading) {
      return (
        <Grid>
          <Grid.Row>
            <Grid.Column width={16}>
              <JobBreadcrumb photoEditingJobId={photoEditingJobId} viewJobsCallback={viewJobsCallback} />
            </Grid.Column>
          </Grid.Row>
          <Grid.Row>
            <Grid.Column width={16} style={{ minHeight: "200px" }}>
              <ContentLoader />
            </Grid.Column>
          </Grid.Row>
        </Grid>
      );
    }

    return (
      <>
        <Modal
          open={modalOpen}
          header={<Header icon="exclamation circle" content="Oops!" />}
          onClose={() => this.closeModal()}
          content={
            <p style={{ padding: "30px" }}>
              Before continuing, please group your images by using the <strong>Automatic Grouping</strong> button or by
              selecting a number of <strong>brackets per photo</strong>, then click the&nbsp;
              <strong>Manual Grouping</strong> button.
            </p>
          }
          actions={[{ key: "ok", content: "Ok", positive: true, onClick: () => this.closeModal() }]}
        />
        <Modal
          open={modalPromptForMoreExposuresOpen}
          header={<Header icon="exclamation circle" content="Oops!" />}
          onClose={() => this.closePromptForMoreExposuresModal()}
          content={
            <p style={{ padding: "30px 30px 20px 30px" }}>
              It looks like you don’t have enough exposures with some images, this will provide poor final images!
              <br />
              Would you like to continue anyway?
            </p>
          }
          actions={[
            {
              key: "cancel",
              content: "I want to add more exposures",
              positive: true,
              onClick: () => showUploadPageCallback(),
            },
            { key: "ok", content: "Continue", positive: false, onClick: () => continueCallback() },
          ]}
        />
        <Grid>
          <Grid.Row>
            <Grid.Column width={16}>
              <JobBreadcrumb
                photoEditingJobId={photoEditingJobId}
                viewJobsCallback={viewJobsCallback || (() => this.redirectToHomePage())}
              />
            </Grid.Column>
          </Grid.Row>
          <Grid.Row textAlign="center">
            <Grid.Column>
              <StepsHeader
                clickCallback={stepsHeaderClickCallback}
                photoEditingJobId={photoEditingJobId}
                canReviewJob={hasPhotos}
                currentStepIndex={Steps.REVIEW_IMAGES}
              />
            </Grid.Column>
          </Grid.Row>
          {/* no-overflow class prevents Select component's list from hiding because of the default "overflow: hidden"
              rule applied to the Row */}
          <Grid.Row textAlign="center" className="no-overflow">
            <Grid.Column>
              <Toolbar
                photosPerImageOptions={ReviewImagesStore.getPhotosPerImagesOptions()}
                sortingMethods={ReviewImagesStore.getSortingMethods()}
                sortingMethod={this.reviewImagesStore.sortingMethod}
                updateSortingMethodCallback={value => this.reviewImagesStore.updateSortingMethod(value)}
                photosPerImage={photosPerImage}
                hasErrors={hasPhotosPerImageErrors}
                enableDelete={hasSelectedPhotos}
                updatePhotosPerImageCallback={value => this.reviewImagesStore.updatePhotosPerImage(value)}
                groupPhotosPerImageCallback={() => this.reviewImagesStore.groupPhotos("photosPerImage")}
                isAutoGroupingEnabled={this.reviewImagesStore.isExifCompatible}
                groupAutoCallback={() => {
                  this.reviewImagesStore.groupPhotos("automatic");
                }}
                deleteCallback={() =>
                  this.reviewImagesStore.deleteSelectedPhotoEditingPhotos().catch(error => showError(error.message))
                }
              />
              {this.reviewImagesStore.hasHdrWarnings && (
                <Message warning>
                  <Message.Header>
                    We noticed one or multiple images you uploaded are not containing enough visual content information
                    (too black or too light).
                  </Message.Header>
                  <p>
                    Those images may affect the resulting image. We suggest removing them before confirming the job.
                    Those images are marked by an <Icon name="warning sign" /> icon.
                  </p>
                  <p>
                    <Button
                      secondary
                      basic
                      onClick={() => {
                        this.reviewImagesStore.selectAllImagesWithWarnings();
                      }}
                      size="mini"
                    >
                      Select all images with warnings
                    </Button>
                  </p>
                </Message>
              )}
              {notEnoughExposuresPerImage && (
                <Message warning>
                  <Message.Header>You don’t have enough exposures with some images</Message.Header>
                  <p>
                    It looks like you don’t have enough exposures with some images, this will provide poor final images!
                    <br />
                    We recommend a minimum of 3 exposures per image.
                  </p>
                </Message>
              )}
              <ReviewImages
                photoEditingImages={photoEditingImages}
                togglePhotoEditingPhotoSelectionCallback={photoEditingPhotoId =>
                  this.reviewImagesStore.togglePhotoEditingPhotoSelection(photoEditingPhotoId)
                }
                reorderPhotoCallback={(source, destination) => this.reviewImagesStore.reorderPhoto(source, destination)}
                clearPhotoEditingPhotosSelectionCallback={() =>
                  this.reviewImagesStore.clearPhotoEditingPhotosSelection()
                }
              />
            </Grid.Column>
          </Grid.Row>
          <Grid.Row textAlign="right">
            <Grid.Column>
              {!hasPhotos ? (
                <Button icon positive primary onClick={() => showUploadPageCallback()}>
                  <Icon name="left arrow" /> Upload Page
                </Button>
              ) : null}
              <PromiseButton
                className={classNames({ loading: isSaving || isSavingPhotosPerImage })}
                type="button"
                primary
                disabled={!hasPhotos}
                onClick={() => this.continue()}
              >
                Continue
              </PromiseButton>
            </Grid.Column>
          </Grid.Row>
        </Grid>
      </>
    );
  }
}

// "match" property provided by react router
ReviewImagesPage.propTypes = {
  showError: PropTypes.func.isRequired,
  match: PropTypes.shape({
    params: PropTypes.shape({
      photoEditingJobId: PropTypes.oneOfType([PropTypes.string, PropTypes.number]).isRequired,
    }).isRequired,
  }),
  // These are passed when scoped to a tour and not in a react-router scope
  photoEditingJobId: PropTypes.number,
  continueCallback: PropTypes.func,
  stepsHeaderClickCallback: PropTypes.func,
  viewJobsCallback: PropTypes.func,
  // triggered when the user deletes all images
  showUploadPageCallback: PropTypes.func,
};

export default ErrorWrapper(ReviewImagesPage);
